import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Loader2, Utensils, Home } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, money } from "@/lib/format";

const sel = "w-full rounded-lg border border-[#E5E0D8] px-3 py-2.5 text-sm outline-none focus:border-[#144F75] bg-white";

export default function ChangeAddOnsModal({ registrationId, onClose }) {
  const qc = useQueryClient();
  const [meal, setMeal] = useState(null);
  const [lodge, setLodge] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["reg-options", registrationId],
    queryFn: () => api.get(`/registrations/${registrationId}/options`).then((r) => r.data),
  });

  const reg = data?.registration;
  const meals = data?.meal_options || [];
  const lodging = data?.lodging_options || [];
  const curMeal = meal ?? reg?.meal_option ?? "";
  const curLodge = lodge ?? reg?.lodging_option ?? "";

  const newTotal = useMemo(() => {
    if (!data) return 0;
    const mp = Number(meals.find((m) => m.label === curMeal)?.price || 0);
    const lp = Number(lodging.find((l) => l.label === curLodge)?.price || 0);
    return Math.round((data.base_per_ticket + mp + lp) * (reg?.quantity || 1) * 100) / 100;
  }, [data, curMeal, curLodge, meals, lodging, reg]);

  const diff = reg ? Math.round((newTotal - Number(reg.price || 0)) * 100) / 100 : 0;
  const changed = reg && (curMeal !== reg.meal_option || curLodge !== reg.lodging_option);

  const apply = async () => {
    setError(""); setSaving(true);
    try {
      const { data: res } = await api.post(`/registrations/${registrationId}/change-options`, {
        meal_option: curMeal, lodging_option: curLodge, origin_url: window.location.origin,
      });
      if (res.checkout?.url) {
        window.location.href = res.checkout.url;
        return;
      }
      qc.invalidateQueries({ queryKey: ["order-history"] });
      toast.success(res.needs_payment ? "Options updated — complete payment to confirm." : "Your add-ons have been updated.");
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail?.message || err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[140] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="change-addons-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} data-testid="change-addons-close" className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        {isLoading || !data ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={26} /></div>
        ) : (
          <>
            <h3 className="font-serif text-xl text-[#1A242B]">Change add-ons</h3>
            <p className="text-sm text-[#4A5D6B] mt-1">{data.event.title} · {fmtDate(data.event.date)}{reg.tier ? ` · ${reg.tier}` : ""}</p>
            {meals.length === 0 && lodging.length === 0 ? (
              <p className="text-sm text-[#4A5D6B] mt-6" data-testid="change-addons-none">This event has no meal or accommodation add-ons.</p>
            ) : (
              <>
                {meals.length > 0 && (
                  <div className="mt-5">
                    <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5 mb-1.5"><Utensils size={14} /> Meal</label>
                    <select value={curMeal} onChange={(e) => setMeal(e.target.value)} data-testid="change-meal-select" className={sel}>
                      <option value="">No meal</option>
                      {meals.map((m) => <option key={m.label} value={m.label}>{m.label} — +{money(Number(m.price))}</option>)}
                    </select>
                  </div>
                )}
                {lodging.length > 0 && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5 mb-1.5"><Home size={14} /> Accommodation</label>
                    <select value={curLodge} onChange={(e) => setLodge(e.target.value)} data-testid="change-lodging-select" className={sel}>
                      <option value="">No accommodation</option>
                      {lodging.map((l) => <option key={l.label} value={l.label}>{l.label} — +{money(Number(l.price))}</option>)}
                    </select>
                  </div>
                )}
                <div className="mt-5 rounded-xl border border-[#E5E0D8] bg-white px-4 py-3 text-sm" data-testid="change-addons-summary">
                  <div className="flex justify-between"><span className="text-[#4A5D6B]">New total</span><span className="font-semibold text-[#144F75]">{money(newTotal)}</span></div>
                  {changed && diff > 0 && (reg.payment_status === "paid" || reg.payment_status === "deposit") && (
                    <p className="text-xs text-[#B5794E] mt-1.5" data-testid="change-addons-due">You'll be charged the {money(diff)} difference now.</p>
                  )}
                  {changed && diff < 0 && (reg.payment_status === "paid" || reg.payment_status === "deposit") && (
                    <p className="text-xs text-[#8FA89B] mt-1.5" data-testid="change-addons-downgrade">Downgrades don't refund automatically — contact us for a refund of {money(Math.abs(diff))}.</p>
                  )}
                </div>
                {error && <p className="text-sm text-red-600 mt-3" data-testid="change-addons-error">{error}</p>}
                <button onClick={apply} disabled={!changed || saving} data-testid="change-addons-apply" className="eyw-btn w-full mt-5 disabled:opacity-60">
                  {saving && <Loader2 className="animate-spin" size={18} />}
                  {changed && diff > 0 && (reg.payment_status === "paid" || reg.payment_status === "deposit") ? `Pay ${money(diff)} & update` : "Update add-ons"}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
